import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMailPreferences } from '../context/MailPreferencesContext';
import {
  getDesktopNotificationPermission,
  isDocumentVisible,
  notifyNewMail,
  toastNewMail,
} from '../utils/desktopNotifications';

const POLL_MS = 8_000;

/**
 * App-weiter Inbox-Poll für Benachrichtigungen — unabhängig davon, ob /inbox offen ist.
 * Sichtbarer Tab: In-App-Toast · ohne Fokus / Hintergrund: Desktop-Notification.
 */
export default function InboxDesktopMailWatcher() {
  const { isAuthenticated, user } = useAuth();
  const { preferences } = useMailPreferences();
  const mailboxKey = String(user?.email || '').trim().toLowerCase();
  const knownUidsRef = useRef(null);
  const inFlightRef = useRef(false);
  const enabledRef = useRef(preferences.desktopNotifications !== false);

  useEffect(() => {
    enabledRef.current = preferences.desktopNotifications !== false;
  }, [preferences.desktopNotifications]);

  useEffect(() => {
    knownUidsRef.current = null;
  }, [mailboxKey]);

  useEffect(() => {
    if (!isAuthenticated || !mailboxKey) return undefined;
    if (preferences.desktopNotifications === false) return undefined;

    let cancelled = false;

    const poll = async () => {
      if (cancelled || inFlightRef.current) return;
      if (!enabledRef.current) return;

      inFlightRef.current = true;
      try {
        const res = await axios.get('/api/emails', {
          params: { limit: 30, folder: 'inbox', refresh: 1 },
          timeout: 45000,
        });
        if (cancelled || !res.data?.success) return;

        const emails = Array.isArray(res.data.emails) ? res.data.emails : [];
        const uids = emails
          .map((e) => (e?.uid != null ? String(e.uid) : ''))
          .filter(Boolean);

        if (knownUidsRef.current == null) {
          knownUidsRef.current = new Set(uids);
          return;
        }

        const newMails = emails.filter(
          (e) => e?.uid != null && !knownUidsRef.current.has(String(e.uid))
        );
        for (const uid of uids) knownUidsRef.current.add(uid);

        // Set nicht unbegrenzt wachsen lassen
        if (knownUidsRef.current.size > 500) {
          knownUidsRef.current = new Set(uids);
        }

        // Auch bei Cache-Responses benachrichtigen, wenn lokal neue UIDs erkannt wurden
        if (newMails.length === 0) return;

        const newest = newMails[0] || {};
        const payload = {
          count: newMails.length,
          subject: newest.subject || '',
          from: newest.from || '',
        };

        // Sichtbarer Tab → In-App-Toast (auch ohne Fenster-Fokus)
        if (isDocumentVisible()) {
          toastNewMail(payload);
        }

        // OS-Toast (überspringt sich selbst, wenn Fenster fokussiert ist)
        if (getDesktopNotificationPermission() === 'granted') {
          notifyNewMail({
            ...payload,
            enabled: true,
          }).catch(() => {});
        }
      } catch (_) {
        /* IMAP ggf. nicht konfiguriert / transient */
      } finally {
        inFlightRef.current = false;
      }
    };

    const intervalId = setInterval(poll, POLL_MS);
    poll();

    const onVis = () => {
      // Nach Hintergrund: schnell nachziehen (Timer können gedrosselt sein)
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAuthenticated, mailboxKey, preferences.desktopNotifications]);

  return null;
}
